import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Layout } from './components/Layout';
import { HomePage } from './pages/HomePage';
import { OrgsPage } from './pages/OrgsPage';
import { InvitationsPage } from './pages/InvitationsPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { TitlesPage } from './pages/TitlesPage';
import { TitleDetailPage } from './pages/TitleDetailPage';
import { SettingsSftpPage } from './pages/SettingsSftpPage';
import { BatchesPage } from './pages/BatchesPage';
import { AdminBatchesPage } from './pages/AdminBatchesPage';
import { SpecPage } from './pages/SpecPage';
import { UploadPage } from './pages/UploadPage';

/**
 * If the user authenticated via the email-verification round trip,
 * Auth0 may consume the original PKCE transaction and drop our
 * appState.returnTo. AcceptInvitePage stashes the token in
 * localStorage before redirecting; we read it back here and route
 * the user to /invite/:token regardless of where they landed.
 */
function PendingInviteRedirect() {
  const location = useLocation();
  const token = window.localStorage.getItem('dfx_pending_invite');
  if (token && !location.pathname.startsWith('/invite/')) {
    return <Navigate to={`/invite/${token}`} replace />;
  }
  return null;
}

function LoginGate() {
  const { loginWithRedirect } = useAuth0();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center">
      <div className="font-mono text-sm text-[color:var(--color-text-muted)]">
        DFX Network · Transfer
      </div>
      <h1 className="text-3xl font-semibold tracking-tight">Sign in to upload content</h1>
      <p className="max-w-md text-[color:var(--color-text-secondary)]">
        Use the email address that received your invitation. New here? Ask your DFX
        contact for an invite.
      </p>
      <button
        type="button"
        onClick={() => loginWithRedirect()}
        className="rounded-md bg-[color:var(--color-crimson)] px-6 py-3 font-medium text-white transition hover:opacity-90"
      >
        Sign in
      </button>
    </div>
  );
}

export function App() {
  const { isAuthenticated, isLoading } = useAuth0();
  if (isLoading) return <div className="p-6">Loading…</div>;

  // Invitation acceptance is the only route that handles its own auth flow.
  return (
    <Routes>
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      <Route path="/auth/callback" element={<div className="p-6">Finalizing your session…</div>} />
      {!isAuthenticated ? (
        <Route path="*" element={<LoginGate />} />
      ) : (
        <Route
          path="*"
          element={
            <>
              <PendingInviteRedirect />
              <Layout>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/orgs" element={<OrgsPage />} />
                  <Route path="/invitations" element={<InvitationsPage />} />
                  <Route path="/titles" element={<TitlesPage />} />
                  <Route path="/titles/:titleId" element={<TitleDetailPage />} />
                  <Route path="/settings/sftp" element={<SettingsSftpPage />} />
                  <Route path="/batches" element={<BatchesPage />} />
                  <Route path="/spec" element={<SpecPage />} />
                  <Route path="/upload" element={<UploadPage />} />
                  <Route path="/admin/batches" element={<AdminBatchesPage />} />
                </Routes>
              </Layout>
            </>
          }
        />
      )}
    </Routes>
  );
}
